import React, {useEffect} from 'react'
import {Box, Link} from '@salesforce/retail-react-app/app/components/shared/ui'
import CloudinaryImage from '../../../cloudinary-media/image'
import PropTypes from 'prop-types'
import Title from './partials/title'
import StoryButton from './partials/button'
import StoryDescription from './partials/description'
import Video from './partials/video'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

export const PlpStoryComponent = (props) => {
    const {
        eagerLoadImage = false,
        componentSize,
        desktopImage,
        mobileImage,
        imageAlt,
        buttonText,
        buttonLink,
        buttonColor,
        title,
        titleColor,
        titleTag,
        titleSizeDesktop,
        titleSizeMobile,
        description,
        descriptionFontFamily,
        descriptionColor,
        desktopVideo,
        desktopVideoThumbnail,
        mobileVideo,
        mobileVideoThumbnail,
        videoAlt,
        titleFontFamily,
        typeId,
        regions,
        id
    } = props
    const allComponents =
        regions?.components?.[0].regions?.flatMap((region) => region.components ?? []) ?? []
    const currentIndex = allComponents.findIndex((c) => c.id === id)

    const shouldDisplayVideo = desktopVideo && mobileVideo
    const pdTagManager = usePdTagManager()
    const componentName = title || 'PLP Story Component'

    useEffect(() => {
        if (!allComponents.length) return
        pdTagManager.viewComponent(typeId, title, allComponents.length)
    }, [])

    return (
        <Box width="100%" overflow="hidden">
            <Box gridArea={componentSize === 'small' ? '300/375' : '608/375.96'}>
                <Link
                    href={buttonLink}
                    cursor={buttonLink ? 'pointer' : 'unset'}
                    onClick={() => {
                        pdTagManager.selectComponent({
                            componentId: typeId || '',
                            componentName: componentName || '',
                            titleName: componentName,
                            titlePosition: currentIndex,
                            linkUrl: buttonLink
                        })
                    }}
                >
                    {shouldDisplayVideo ? (
                        <Video
                            desktopVideo={desktopVideo}
                            mobileVideo={mobileVideo || desktopVideo}
                            desktopVideoThumbnail={desktopVideoThumbnail}
                            mobileVideoThumbnail={mobileVideoThumbnail}
                            videoAlt={videoAlt}
                            aspectRatio={componentSize === 'small' ? '300/375' : '608/375.96'}
                            cloudinaryAspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                        />
                    ) : (
                        <>
                            <CloudinaryImage
                                src={desktopImage}
                                aspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                                styles={{
                                    display: {base: 'none', md: 'block'},
                                    aspectRatio:
                                        componentSize === 'small' ? '300/375' : '608/375.96'
                                }}
                                imgSizes={{
                                    md: componentSize === 'small' ? '25vw' : '50vw',
                                    base: componentSize === 'small' ? '50vw' : '100vw'
                                }}
                                sizes={
                                    componentSize === 'small'
                                        ? [225, 325, 384, 576, 650, 768, 960]
                                        : [450, 650, 768, 1152, 1290, 1536, 1920]
                                }
                                alt={imageAlt}
                                lazyload={!eagerLoadImage}
                            />
                            <CloudinaryImage
                                src={mobileImage}
                                aspectRatio={componentSize === 'small' ? '4:5' : '16:10'}
                                styles={{
                                    display: {base: 'block', md: 'none'},
                                    aspectRatio:
                                        componentSize === 'small' ? '300/375' : '608/375.96'
                                }}
                                imgSizes={{
                                    md: componentSize === 'small' ? '25vw' : '50vw',
                                    base: componentSize === 'small' ? '50vw' : '100vw'
                                }}
                                sizes={
                                    componentSize === 'small'
                                        ? [225, 325, 384, 576, 650, 768, 960]
                                        : [450, 650, 768, 1152, 1290, 1536, 1920]
                                }
                                alt={imageAlt}
                                lazyload={!eagerLoadImage}
                            />
                        </>
                    )}
                </Link>
            </Box>
            <Box
                textAlign="left"
                margin={{
                    base: componentSize === 'small' ? '0 20px' : '0 30px',
                    md: componentSize === 'small' ? '0 20px' : '0 30px',
                    lg: componentSize === 'small' ? '0 20px' : '0 30px',
                    xl: '0 30px'
                }}
            >
                {title && (
                    <Title
                        title={title}
                        color={titleColor}
                        tag={titleTag}
                        titleSizeDesktop={titleSizeDesktop}
                        titleSizeMobile={titleSizeMobile}
                        titleFontFamily={titleFontFamily}
                    />
                )}
                {description && (
                    <StoryDescription
                        description={description}
                        descriptionFontFamily={descriptionFontFamily}
                        descriptionColor={descriptionColor}
                    />
                )}
                {buttonText && (
                    <StoryButton
                        buttonLink={buttonLink}
                        buttonText={buttonText}
                        buttonColor={buttonColor}
                    />
                )}
            </Box>
        </Box>
    )
}

PlpStoryComponent.displayName = 'PlpStoryComponent'

PlpStoryComponent.propTypes = {
    eagerLoadImage: PropTypes.bool,
    id: PropTypes.string,
    componentSize: PropTypes.string,
    desktopImage: PropTypes.string,
    mobileImage: PropTypes.string,
    imageAlt: PropTypes.string,
    buttonText: PropTypes.string,
    buttonLink: PropTypes.string,
    buttonColor: PropTypes.string,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    titleTag: PropTypes.string,
    titleSizeDesktop: PropTypes.string,
    titleSizeMobile: PropTypes.string,
    titleSizeTablet: PropTypes.string,
    description: PropTypes.string,
    descriptionColor: PropTypes.string,
    descriptionFontFamily: PropTypes.string,
    desktopVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideo: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    titleFontFamily: PropTypes.string,
    videoAlt: PropTypes.string,
    typeId: PropTypes.string,
    regions: PropTypes.object
}

export default PlpStoryComponent
